export type CustomerStatus = 'active' | 'merged';

export interface CustomerMutationContext {
  readonly actorId: string;
  readonly idempotencyKey: string;
  readonly at: string;
}

export interface CreateCustomerCommand {
  readonly phone: string;
  readonly displayName?: string | null;
}

export interface RepairCustomerPhoneCommand {
  readonly customerId: string;
  readonly expectedVersion: number;
  readonly phone: string;
  readonly reason: string;
}

export interface MergeCustomerCommand {
  readonly sourceCustomerId: string;
  readonly targetCustomerId: string;
  readonly expectedSourceVersion: number;
  readonly expectedTargetVersion: number;
  readonly reason: string;
}

export interface CustomerSnapshot {
  readonly customerId: string;
  readonly phone: string;
  readonly displayName: string | null;
  readonly status: CustomerStatus;
  readonly version: number;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
  readonly mergedIntoCustomerId?: string;
}

export interface CustomerMergePreview {
  readonly source: CustomerSnapshot;
  readonly target: CustomerSnapshot;
  readonly canonicalSourceCustomerId: string;
  readonly canonicalTargetCustomerId: string;
}

export interface CustomerMergeResult {
  readonly source: CustomerSnapshot;
  readonly target: CustomerSnapshot;
}

export interface CustomerAuditEvent {
  readonly type: 'customer.created' | 'customer.phone.repaired' | 'customer.merged';
  readonly customerId: string;
  readonly at: string;
  readonly actorId: string;
  readonly version: number;
  readonly reason?: string;
  readonly previousPhone?: string;
  readonly phone?: string;
  readonly sourceCustomerId?: string;
  readonly targetCustomerId?: string;
}

interface StoredCustomer {
  readonly customerId: string;
  phone: string;
  displayName: string | null;
  status: CustomerStatus;
  version: number;
  readonly createdAt: string;
  readonly createdBy: string;
  updatedAt: string;
  updatedBy: string;
  mergedIntoCustomerId?: string;
}

interface IdempotentResult {
  readonly signature: string;
  readonly result: unknown;
}

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (kind: string, context: CustomerMutationContext, command: unknown): string =>
  JSON.stringify(stableValue({ kind, context, command }));

const assertText = (value: string, code: string): string => {
  const normalized = value?.trim();
  if (!normalized) throw new Error(code);
  return normalized;
};

const assertTime = (value: string): void => {
  if (!Number.isFinite(Date.parse(value))) throw new Error('CUSTOMER_TIMESTAMP_INVALID');
};

const assertVersion = (value: number): void => {
  if (!Number.isSafeInteger(value) || value < 1) throw new Error('CUSTOMER_EXPECTED_VERSION_INVALID');
};

export function normalizeCustomerPhone(raw: string): string {
  const value = assertText(raw, 'CUSTOMER_PHONE_REQUIRED');
  const compact = value.replace(/[\s()\-.]/g, '');

  if (compact.startsWith('+')) {
    if (!/^\+[1-9]\d{7,14}$/.test(compact)) throw new Error('CUSTOMER_PHONE_INVALID');
    return compact;
  }

  if (/^[2-9]\d{7}$/.test(compact)) return `+852${compact}`;
  if (/^852[2-9]\d{7}$/.test(compact)) return `+${compact}`;
  if (/^\d{9,15}$/.test(compact)) throw new Error('CUSTOMER_PHONE_AMBIGUOUS');
  throw new Error('CUSTOMER_PHONE_INVALID');
}

export function createCustomerAuthority() {
  const customers = new Map<string, StoredCustomer>();
  const phoneOwners = new Map<string, string>();
  const idempotency = new Map<string, IdempotentResult>();
  const auditEvents: CustomerAuditEvent[] = [];
  let sequence = 0;

  const snapshot = (customer: StoredCustomer): CustomerSnapshot => clone({
    customerId: customer.customerId,
    phone: customer.phone,
    displayName: customer.displayName,
    status: customer.status,
    version: customer.version,
    createdAt: customer.createdAt,
    createdBy: customer.createdBy,
    updatedAt: customer.updatedAt,
    updatedBy: customer.updatedBy,
    ...(customer.mergedIntoCustomerId ? { mergedIntoCustomerId: customer.mergedIntoCustomerId } : {}),
  });

  const assertContext = (context: CustomerMutationContext): void => {
    assertText(context.actorId, 'CUSTOMER_ACTOR_REQUIRED');
    assertText(context.idempotencyKey, 'CUSTOMER_IDEMPOTENCY_REQUIRED');
    assertTime(context.at);
  };

  const requireCustomer = (customerId: string): StoredCustomer => {
    const accepted = assertText(customerId, 'CUSTOMER_ID_REQUIRED');
    const customer = customers.get(accepted);
    if (!customer) throw new Error('CUSTOMER_NOT_FOUND');
    return customer;
  };

  const canonicalId = (customerId: string): string => {
    let current = requireCustomer(customerId);
    const seen = new Set<string>();
    while (current.status === 'merged') {
      if (!current.mergedIntoCustomerId || seen.has(current.customerId)) throw new Error('CUSTOMER_MERGE_CHAIN_INVALID');
      seen.add(current.customerId);
      current = requireCustomer(current.mergedIntoCustomerId);
    }
    return current.customerId;
  };

  const replay = <T>(kind: string, context: CustomerMutationContext, command: unknown, action: () => T): T => {
    assertContext(context);
    const signature = signatureOf(kind, context, command);
    const previous = idempotency.get(context.idempotencyKey);
    if (previous) {
      if (previous.signature !== signature) throw new Error('CUSTOMER_IDEMPOTENCY_CONFLICT');
      return clone(previous.result as T);
    }
    const result = action();
    idempotency.set(context.idempotencyKey, { signature, result: clone(result) });
    return clone(result);
  };

  const assertPhoneAvailable = (phone: string, allowedCustomerId?: string): void => {
    const owner = phoneOwners.get(phone);
    if (!owner) return;
    const canonicalOwner = canonicalId(owner);
    if (allowedCustomerId && canonicalOwner === canonicalId(allowedCustomerId)) return;
    throw new Error('CUSTOMER_PHONE_CONFLICT');
  };

  return {
    create(context: CustomerMutationContext, command: CreateCustomerCommand): CustomerSnapshot {
      return replay('create', context, command, () => {
        const phone = normalizeCustomerPhone(command.phone);
        assertPhoneAvailable(phone);
        const displayName = command.displayName?.trim() || null;
        const customerId = `customer:${++sequence}`;
        const stored: StoredCustomer = {
          customerId,
          phone,
          displayName,
          status: 'active',
          version: 1,
          createdAt: context.at,
          createdBy: context.actorId.trim(),
          updatedAt: context.at,
          updatedBy: context.actorId.trim(),
        };
        customers.set(customerId, stored);
        phoneOwners.set(phone, customerId);
        auditEvents.push({
          type: 'customer.created', customerId, at: context.at, actorId: context.actorId.trim(), version: 1, phone,
        });
        return snapshot(stored);
      });
    },

    get(customerId: string): CustomerSnapshot | null {
      const customer = customers.get(customerId.trim());
      return customer ? snapshot(customer) : null;
    },

    findByPhone(phoneInput: string): CustomerSnapshot | null {
      const phone = normalizeCustomerPhone(phoneInput);
      const owner = phoneOwners.get(phone);
      if (!owner) return null;
      return snapshot(requireCustomer(canonicalId(owner)));
    },

    canonicalCustomerId(customerId: string): string {
      return canonicalId(customerId);
    },

    repairPhone(context: CustomerMutationContext, command: RepairCustomerPhoneCommand): CustomerSnapshot {
      return replay('repairPhone', context, command, () => {
        assertVersion(command.expectedVersion);
        const reason = assertText(command.reason, 'CUSTOMER_REASON_REQUIRED');
        const customer = requireCustomer(command.customerId);
        if (customer.status !== 'active') throw new Error('CUSTOMER_NOT_ACTIVE');
        if (customer.version !== command.expectedVersion) throw new Error('CUSTOMER_VERSION_CONFLICT');
        const phone = normalizeCustomerPhone(command.phone);
        assertPhoneAvailable(phone, customer.customerId);
        const previousPhone = customer.phone;
        if (phone === previousPhone) throw new Error('CUSTOMER_PHONE_UNCHANGED');
        phoneOwners.delete(previousPhone);
        phoneOwners.set(phone, customer.customerId);
        customer.phone = phone;
        customer.version += 1;
        customer.updatedAt = context.at;
        customer.updatedBy = context.actorId.trim();
        auditEvents.push({
          type: 'customer.phone.repaired', customerId: customer.customerId, at: context.at,
          actorId: context.actorId.trim(), version: customer.version, reason, previousPhone, phone,
        });
        return snapshot(customer);
      });
    },

    prepareMerge(command: Omit<MergeCustomerCommand, 'reason'>): CustomerMergePreview {
      assertVersion(command.expectedSourceVersion);
      assertVersion(command.expectedTargetVersion);
      if (command.sourceCustomerId === command.targetCustomerId) throw new Error('CUSTOMER_MERGE_SELF');
      const source = requireCustomer(command.sourceCustomerId);
      const target = requireCustomer(command.targetCustomerId);
      if (source.status !== 'active') throw new Error('CUSTOMER_SOURCE_NOT_ACTIVE');
      if (target.status !== 'active') throw new Error('CUSTOMER_TARGET_NOT_ACTIVE');
      if (source.version !== command.expectedSourceVersion || target.version !== command.expectedTargetVersion) {
        throw new Error('CUSTOMER_VERSION_CONFLICT');
      }
      return {
        source: snapshot(source),
        target: snapshot(target),
        canonicalSourceCustomerId: canonicalId(source.customerId),
        canonicalTargetCustomerId: canonicalId(target.customerId),
      };
    },

    merge(context: CustomerMutationContext, command: MergeCustomerCommand): CustomerMergeResult {
      return replay('merge', context, command, () => {
        const reason = assertText(command.reason, 'CUSTOMER_REASON_REQUIRED');
        const preview = this.prepareMerge(command);
        const source = requireCustomer(preview.source.customerId);
        const target = requireCustomer(preview.target.customerId);

        source.status = 'merged';
        source.mergedIntoCustomerId = target.customerId;
        source.version += 1;
        source.updatedAt = context.at;
        source.updatedBy = context.actorId.trim();
        target.version += 1;
        target.updatedAt = context.at;
        target.updatedBy = context.actorId.trim();

        phoneOwners.set(source.phone, target.customerId);
        phoneOwners.set(target.phone, target.customerId);
        auditEvents.push({
          type: 'customer.merged', customerId: target.customerId, sourceCustomerId: source.customerId,
          targetCustomerId: target.customerId, at: context.at, actorId: context.actorId.trim(),
          version: target.version, reason,
        });
        return { source: snapshot(source), target: snapshot(target) };
      });
    },

    audit(): readonly CustomerAuditEvent[] {
      return auditEvents.map((event) => clone(event));
    },
  };
}
